// Necessary polyfills to support IE11. See babel.config.js for details
import 'core-js-bundle';
import 'regenerator-runtime/runtime';
import 'custom-event-polyfill';

import Vue from 'vue';
import BootstrapVue from 'bootstrap-vue';
import {library} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon, FontAwesomeLayers} from '@fortawesome/vue-fontawesome';
import App from './App.vue';
import {routerBuilder} from './router';
import {storeBuilder} from './store';
import {authBuilder} from './auth';
import {httpBuilder} from './http';
import PulseLoader from 'vue-spinner/src/PulseLoader.vue';
import {fas} from '@fortawesome/free-solid-svg-icons';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import pluralize from 'pluralize';
import VTour from './components/Tour/VTour';
import VStep from './components/Tour/VStep';
import OverlaySpinner from './components/OverlaySpinner';
import VueScrollTo from 'vue-scrollto';
import VueClipboard from 'vue-clipboard2';
import VueLazyload from 'vue-lazyload';
import RunVersionCell from './components/Table/RunVersionCell';
import ListFilter from './components/Table/ListFilter';
import SelectedFilter from './components/Table/SelectedFilter';
import {getConfig} from './config';
import Landing from './components/Static/Landing';
import Logos from './components/Static/Logos';
import Brand from './components/Static/Brand';
import VueAnalytics from 'vue-analytics';
import moment from 'moment';
import compareVersions from 'compare-versions';
import AsyncComputed from 'vue-async-computed';

(async function () {
  let config = await getConfig();
  if (config.maintenance) {
    const msg = config.maintenance.message || 'Application is currently under maintenance. Please retry later. Sorry for any inconvenience!';
    alert(msg);
    throw new Error(msg);
  }
  if (config && !config.baseUrl) {
    // fetch config from auth server if no base API URL parameter is set
    const serverConfig = await getConfig(`${config.authUrl}/applications/${config.database}/config`);
    if (serverConfig === undefined) {
      const msg = 'Configuration not available on Vue initialization. \n\nMake sure you there is the config file "public/config.json"';
      alert(msg);
      throw new Error(msg);
    }
    // replace all available values from server config while keeping unique from content repository one
    config = {...config, ...serverConfig};
  }

  // change this when updating config structure/version
  const supportedVersion = '2.3';
  const configVersion = config.configVersion || '1.0';
  if (compareVersions(configVersion, supportedVersion) !== 0) {
    const msg = `Configuration version ${configVersion} is not compatible with current version of Scenario Explorer application!`;
    alert(msg);
    throw new Error(msg);
  }

  // uncomment to output effective config to console (to use for development copy it to public/config.json, see README)
  // console.log('config', JSON.stringify(config, null, '  '));

  const ua = window.navigator.userAgent;
  const msie = ua.indexOf('MSIE ');
  if (msie >= 0 || ua.match(/Trident.*rv:11\./)) {
    alert(`Internet Explorer is currently not supported by the ${config.productName}. We apologize for any inconvenience! For better user experience use most recent Firefox, Chrome or Safari`);
  }

  library.add(fas);
  Object.defineProperty(Vue.prototype, '$config', {value: config});
  // noinspection ES6ModulesDependencies
  Object.defineProperty(Vue.prototype, '$version', {value: VERSION});
  Object.defineProperty(Vue.prototype, '$tours', {value: {}});
  Vue.use(BootstrapVue);
  Vue.use(VueClipboard);
  Vue.use(VueLazyload, {
    lazyComponent: true,
  });
  Vue.use(AsyncComputed);
  Vue.component('icon', FontAwesomeIcon);
  Vue.component('fa-layers', FontAwesomeLayers);
  Vue.component('PulseLoader', PulseLoader);
  Vue.component('VTour', VTour);
  Vue.component('VStep', VStep);
  Vue.component('RunVersionCell', RunVersionCell);
  Vue.component('ListFilter', ListFilter);
  Vue.component('SelectedFilter', SelectedFilter);
  Vue.component('Landing', Landing);
  Vue.component('Logos', Logos);
  Vue.component('Brand', Brand);
  Vue.component('OverlaySpinner', OverlaySpinner);

  Vue.directive('access', {
    inserted(el, binding, vnode) {
      const target = vnode.context.$router.resolve(vnode.componentInstance.$options.propsData.to);
      if (target) {
        const hasAccess = target.route.meta.checkAccess(vnode.context.$auth);
        if (!hasAccess) {
          el.style.display = 'none';
        }
      }
    }
  });

  Vue.filter('pluralize', pluralize);
  Vue.filter('formatDate', value => value ? moment(value).format('YYYY-MM-DD HH:mm') : '');
  Vue.use(VueScrollTo, {easing: 'ease-in-out', duration: 2000});
  Vue.config.productionTip = false;
  Vue.config.devtools = true;
  // Vue.config.performance = true;

  const http = httpBuilder(config);
  const store = await storeBuilder(http, config);
  const auth = authBuilder(http, store, config);
  const router = routerBuilder(auth, config);
  http.enableInterceptors(router, auth, store);
  if (config.analyticsPropertyId) {
    Vue.use(VueAnalytics, {id: config.analyticsPropertyId, router});
  }
  const render = h => h(App);
  new Vue({render, store, router}).$mount('#app');
})();
